import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { usePlan } from '../context/PlanContext';
import { useStripe } from '../context/StripeContext';
import AppShell from '../components/AppShell';
import SEO from '../components/SEO';
import BillingManagementModal from '../components/billing/BillingManagementModal';

import {
  ArrowCircleUp,
  ArrowSquareOut,
  Calendar,
  Check,
  CircleNotch,
  CreditCard,
  Crown,
  GearSix,
  Lightning,
  Sparkle,
  Star
} from '@phosphor-icons/react';
const Billing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { plan, planLimits, refreshPlan } = usePlan();
  const { createSubscriptionCheckout, pollPaymentStatus, loading: stripeLoading } = useStripe();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(null);

  const [creditPurchasing, setCreditPurchasing] = useState(null);
  const [voicePurchasing, setVoicePurchasing] = useState(null);

  // Handle payment callback
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const isCreditPurchase = searchParams.get('credit_purchase');
    
    if (isCreditPurchase && sessionId) {
      handleCreditPurchaseSuccess(sessionId);
      setSearchParams({});
    } else if (paymentStatus === 'success' && sessionId) {
      handlePaymentSuccess(sessionId);
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. No charges were made.');
      setSearchParams({});
    }
  }, [searchParams]);

  const handleCreditPurchaseSuccess = async (sessionId) => {
    setProcessing(true);
    try {
      const result = await pollPaymentStatus(sessionId);
      if (result.success) {
        toast.success('Credits purchased successfully!');
        fetchUserData();
      } else {
        toast.error(result.error || 'Credit purchase verification failed.');
      }
    } catch (error) {
      console.error('Credit purchase verification error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    setProcessing(true);
    try {
      const result = await pollPaymentStatus(sessionId);
      if (result.success) {
        toast.success('Subscription activated successfully!');
        if (refreshPlan) await refreshPlan();
        fetchUserData();
      } else {
        toast.error(result.error || 'Payment verification failed.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/account/profile`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (targetPlan) => {
    const planId = `${targetPlan}_monthly`;
    setUpgradingPlan(targetPlan);
    setProcessing(true);
    try {
      await createSubscriptionCheckout(planId);
      // User will be redirected to Stripe
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.message || 'Failed to upgrade plan');
      setUpgradingPlan(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleCreditPurchase = async (packageId) => {
    setCreditPurchasing(packageId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-credit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create checkout');
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to purchase credits');
    } finally {
      setCreditPurchasing(null);
    }
  };

  const handleVoicePurchase = async (packageId) => {
    setVoicePurchasing(packageId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-voice-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create checkout');
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to purchase voice minutes');
    } finally {
      setVoicePurchasing(null);
    }
  };

  const handleManageBilling = async () => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/billing/portal`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        toast.error('Failed to access billing portal');
      }
    } catch (error) {
      console.error('Error accessing portal:', error);
      toast.error('Failed to access billing portal');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanIcon = (planType) => {
    if (planType === 'partner_pro' || planType === 'partner-pro') return Crown;
    if (planType === 'teams' || planType === 'pro') return Star;
    if (planType === 'professional' || planType === 'builder') return Sparkle;
    return Lightning;
  };

  const getPlanGradient = (planType) => {
    if (planType === 'partner_pro' || planType === 'partner-pro') return 'from-[#EC4899] to-[#8B5CF6]';
    if (planType === 'teams' || planType === 'pro') return 'from-[#6366F1] to-[#8B5CF6]';
    if (planType === 'professional' || planType === 'builder') return 'from-[#10B981] to-[#6366F1]';
    return 'from-[#718096] to-[#A0AEC0]';
  };

  const creditPackages = [
    { id: 'credits_1000', credits: '1,000', price: '$29' },
    { id: 'credits_5000', credits: '5,000', price: '$99' },
    { id: 'credits_20000', credits: '20,000', price: '$299' },
  ];

  const voicePackages = [
    { id: 'voice_100', minutes: '100', price: '$19' },
    { id: 'voice_500', minutes: '500', price: '$79' },
    { id: 'voice_1500', minutes: '1,500', price: '$199' },
  ];

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 'Free',
      description: 'Get started with zero risk',
      icon: Lightning,
      gradient: 'from-[#718096] to-[#A0AEC0]',
      features: [
        'Answer enquiries automatically',
        '100 credits included monthly',
        '1 user',
        'Community support',
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$39/mo',
      description: 'For growing businesses',
      icon: Sparkle,
      gradient: 'from-[#10B981] to-[#6366F1]',
      features: [
        'Everything in Basic',
        '2,000 credits per month',
        '30 voice minutes included',
        'Advanced task engine (Orion)',
        'Priority email support',
      ]
    },
    {
      id: 'teams',
      name: 'Teams',
      price: '$99/mo',
      description: 'For teams that move fast',
      icon: Star,
      gradient: 'from-[#6366F1] to-[#8B5CF6]',
      popular: true,
      features: [
        'Everything in Professional',
        '6,000 credits per month',
        '100 voice minutes included',
        'Up to 10 team members',
        'Priority support',
      ]
    },
    {
      id: 'partner_pro',
      name: 'Partner Pro',
      price: '$299/mo',
      description: 'For agencies & power users',
      icon: Crown,
      gradient: 'from-[#EC4899] to-[#8B5CF6]',
      features: [
        'Everything in Teams',
        '20,000 credits per month',
        '300 voice minutes included',
        'Multi-tenant management',
        'Bring your own LLM keys',
        'Agent Fabric - External agent management',
        'Business analysis and reporting',
        'Dedicated support',
      ]
    }
  ];

  const currentPlan = plans.find(p => p.id === plan) || plans[0];
  const Icon = currentPlan.icon;

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <CircleNotch size={32} className="text-white animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <SEO title="Billing - Man & Machine" />
      
      

      <div className="container mx-auto">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Billing & Subscription
          </h1>
          <p className="text-[#A0AEC0] text-lg">
            Manage your plan and billing settings
          </p>
        </motion.div>

        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-3xl p-8 mb-12 border border-[rgba(99,102,241,0.2)]"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${currentPlan.gradient} flex items-center justify-center flex-shrink-0`}>
                <Icon size={32} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">{currentPlan.name} Plan</h2>
                  {currentPlan.popular && (
                    <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#14F195] to-[#6366F1] text-white text-xs font-semibold">
                      Popular
                    </span>
                  )}
                </div>
                <p className="text-[#A0AEC0] mb-2">{currentPlan.description}</p>
                {user?.plan_renews_at && (
                  <div className="flex items-center gap-2 text-sm text-[#718096]">
                    <Calendar size={14} />
                    <span>Renews on {new Date(user.plan_renews_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {plan !== 'starter' && plan !== 'basic' && plan !== 'free' && (
                <button
                  onClick={() => setShowBillingModal(true)}
                  disabled={processing}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                >
                  <GearSix size={18} />
                  Manage Billing
                </button>
              )}
              {(plan === 'starter' || plan === 'basic' || plan === 'free') && (
                <button
                  onClick={() => handleUpgrade('professional')}
                  disabled={processing}
                  className="relative overflow-hidden rounded-full group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#10B981] to-[#6366F1]"></div>
                  <span className="relative z-10 flex items-center gap-2 px-6 py-3 text-white font-semibold">
                    {processing ? (
                      <>
                        <CircleNotch size={18} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ArrowCircleUp size={18} />
                        Upgrade to Professional
                      </>
                    )}
                  </span>
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Plan Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-3xl font-bold text-white mb-2">All Plans</h2>
          <p className="text-[#A0AEC0]">Choose the plan that fits your needs</p>
          {/* Helper text for plan changes */}
          {plan !== 'starter' && plan !== 'basic' && plan !== 'free' && (
            <p className="text-sm text-[#6366F1] mt-2 flex items-center gap-2">
              <GearSix size={14} />
              Click &apos;Manage Billing&apos; above to change your plan, manage seats, or cancel.
            </p>
          )}
        </motion.div>

        {/* Plan Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((planOption, index) => {
            const PlanIcon = planOption.icon;
            const isCurrentPlan = planOption.id === plan || (plan === 'basic' && planOption.id === 'basic') || (plan === 'starter' && planOption.id === 'basic');
            const planOrder = { 'basic': 0, 'starter': 0, 'professional': 1, 'builder': 1, 'teams': 2, 'pro': 2, 'partner_pro': 3 };
            const currentOrder = planOrder[plan] ?? 0;
            const optionOrder = planOrder[planOption.id] ?? 0;
            const canUpgrade = optionOrder > currentOrder;

            return (
              <motion.div
                key={planOption.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className={`glass rounded-3xl p-6 flex flex-col ${
                  planOption.popular 
                    ? 'border-2 border-[#6366F1]' 
                    : 'border border-[rgba(255,255,255,0.1)]'
                }`}
              >
                {planOption.popular && (
                  <div className="mb-4">
                    <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white text-xs font-semibold flex items-center gap-1 w-fit" data-testid="pro-most-popular-badge">
                      <Star size={12} fill="white" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${planOption.gradient} flex items-center justify-center mb-4`}>
                  <PlanIcon size={24} className="text-white" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-1">{planOption.name}</h3>
                {planOption.subtitle && (
                  <p className="text-sm text-[#14F195] font-medium mb-1">{planOption.subtitle}</p>
                )}
                <p className="text-3xl font-bold text-white mb-2">{planOption.price}</p>
                <p className="text-[#A0AEC0] text-sm mb-6">{planOption.description}</p>

                <ul className="space-y-3 mb-6 flex-grow">
                  {planOption.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                      <span className="text-[#A0AEC0] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                {isCurrentPlan ? (
                  <div className="py-3 rounded-full bg-gradient-to-r from-[#14F195]/20 to-[#14F195]/10 border border-[#14F195]/30 text-[#14F195] font-semibold text-center flex items-center justify-center gap-2" data-testid={`plan-current-${planOption.id}`}>
                    <Check size={18} />
                    Current Plan
                  </div>
                ) : canUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(planOption.id)}
                    disabled={processing}
                    className="w-full relative overflow-hidden rounded-full group"
                    data-testid={`plan-upgrade-${planOption.id}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${planOption.gradient}`}></div>
                    <span className="relative z-10 py-3 text-white font-semibold block">
                      {upgradingPlan === planOption.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <CircleNotch size={18} className="animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        `Upgrade to ${planOption.name}`
                      )}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowBillingModal(true)}
                    className="w-full py-3 rounded-full glass border border-[rgba(255,255,255,0.1)] text-[#A0AEC0] font-medium hover:bg-[rgba(255,255,255,0.05)] hover:text-white transition-colors flex items-center justify-center gap-2"
                  >
                    <GearSix size={16} />
                    Manage Billing to Change
                  </button>
                )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Buy Additional Credits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12"
        >
          <h2 className="text-3xl font-bold text-white mb-2">Buy Additional Credits</h2>
          <p className="text-[#A0AEC0] mb-6">Need more AI compute? Top up your credits instantly.</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.1)] hover:border-[rgba(99,102,241,0.3)] transition-colors"
                data-testid={`credit-package-${pkg.id}`}
              >
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-white mb-1">{pkg.credits}</div>
                  <div className="text-sm text-[#A0AEC0]">credits</div>
                </div>
                <div className="text-center mb-5">
                  <span className="text-2xl font-bold text-[#14F195]">{pkg.price}</span>
                </div>
                <button
                  onClick={() => handleCreditPurchase(pkg.id)}
                  disabled={creditPurchasing === pkg.id}
                  className="w-full py-3 rounded-full glass border border-[rgba(99,102,241,0.4)] text-white font-semibold hover:bg-[rgba(99,102,241,0.15)] transition-colors flex items-center justify-center gap-2"
                  data-testid={`buy-credits-${pkg.id}`}
                >
                  {creditPurchasing === pkg.id ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} />
                      Buy Credits
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Buy Voice Minutes Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="mt-12"
        >
          <h2 className="text-3xl font-bold text-white mb-2">Buy Voice Minutes</h2>
          <p className="text-[#A0AEC0] mb-6">Power your AI voice agents with additional talk time.</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {voicePackages.map((pkg) => (
              <div
                key={pkg.id}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.1)] hover:border-[rgba(236,72,153,0.3)] transition-colors"
                data-testid={`voice-package-${pkg.id}`}
              >
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-white mb-1">{pkg.minutes}</div>
                  <div className="text-sm text-[#A0AEC0]">minutes</div>
                </div>
                <div className="text-center mb-5">
                  <span className="text-2xl font-bold text-[#EC4899]">{pkg.price}</span>
                </div>
                <button
                  onClick={() => handleVoicePurchase(pkg.id)}
                  disabled={voicePurchasing === pkg.id}
                  className="w-full py-3 rounded-full glass border border-[rgba(236,72,153,0.4)] text-white font-semibold hover:bg-[rgba(236,72,153,0.15)] transition-colors flex items-center justify-center gap-2"
                  data-testid={`buy-voice-${pkg.id}`}
                >
                  {voicePurchasing === pkg.id ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} />
                      Buy Minutes
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Enterprise callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 glass rounded-2xl p-8 border border-[rgba(255,255,255,0.08)] text-center"
          data-testid="enterprise-callout"
        >
          <h3 className="text-xl font-bold text-white mb-2">Need a custom plan?</h3>
          <p className="text-[#A0AEC0] text-sm max-w-lg mx-auto mb-5">
            Enterprise plans include custom credit & voice allowances, dedicated support, onboarding & training, and custom integrations.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="px-6 py-3 rounded-full glass border border-[rgba(255,255,255,0.15)] text-white font-semibold hover:bg-[rgba(255,255,255,0.05)] transition-colors"
            data-testid="enterprise-contact-btn"
          >
            Contact Us
          </button>
        </motion.div>
      </div>

      {/* Billing Management Modal */}
      <BillingManagementModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        currentPlan={plan}
        onPlanChange={(newPlan) => {
          refreshPlan();
          setShowBillingModal(false);
        }}
      />
    </AppShell>
  );
};

export default Billing;
